import { useQuery } from '@tanstack/react-query';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import Api from '@barboxx/api';
import { CustomDrink } from '@barboxx/models/types';

interface Props {
    disabled?: boolean;
}

interface BaseCustomDrinkWatchProps {
    customDrink?: CustomDrink;
    customDrinkId?: number;
    businessId?: number;
}

export function CustomDrinkInput({ disabled = false }: Props) {
    const { customDrinkId, customDrink, businessId } =
        useFormContext<BaseCustomDrinkWatchProps>().getValues();

    const customDrinkQueryResult = useQuery({
        queryKey: ['customDrinks', customDrinkId, businessId],
        queryFn: () =>
            Api.CustomDrinks.CustomDrink.getCustomDrinks({
                page: 0,
                size: 1000,
                sort: 'name,asc',
                businessId,
            }),
        initialData: {
            ok: true,
            problem: null,
            originalError: null,
            data: {
                results: customDrink ? [customDrink] : [],
                total: 1,
                isPaginated: false,
            },
        },
    });

    return (
        <AutocompleteElement
            label="Custom Drink"
            name="customDrinkId"
            loading={customDrinkQueryResult.isLoading}
            multiple={false}
            options={customDrinkQueryResult.data?.data?.results ?? []}
            transform={{
                output(event, value, reason, details) {
                    if (reason === 'selectOption') {
                        return typeof value === 'string' ? value : value?.id;
                    }
                    if (reason === 'removeOption') {
                        return null;
                    }
                    return value;
                },
            }}
            autocompleteProps={{
                fullWidth: true,
                readOnly: disabled,
                disabled: disabled,
                isOptionEqualToValue(
                    option?: CustomDrink,
                    value?: CustomDrink | number | null
                ) {
                    const isNumber = typeof value === 'number';

                    if (isNumber) {
                        return option?.id === value;
                    } else {
                        return option?.id === (value?.id ?? value);
                    }
                },
                getOptionLabel(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.name}`;
                },
                getOptionKey(option) {
                    if (typeof option === 'string') {
                        return option;
                    }
                    return `${option?.id}`;
                },
            }}
        />
    );
}
